/**
 * Feature access — the client half of backend `services/feature_gates.py`.
 *
 * The matrix is SERVED, not duplicated: `GET /auth/me` returns `tier` and
 * `features: {key: {allowed, limit}}` for the caller's DB-resolved role, and
 * this store caches it. `RequireAuth` hydrates it on every app entry; the axios
 * interceptor refreshes it when the API answers 403 `feature_denied`.
 *
 * Use `useFeature('p2p.intel')` to lock UI the API would refuse (the sidebar
 * shows gated items locked rather than hiding them). The backend is
 * authoritative — locking is a courtesy, never the control.
 */
import { useSyncExternalStore } from 'react';
import { create } from 'zustand';

export type FeatureKey =
    | 'workbench.triage'
    | 'workbench.trace'
    | 'cases.own'
    | 'reports.view'
    | 'demo'
    | 'feedback'
    | 'exports.basic'
    | 'investigation.auto'
    | 'osint.live_paid'
    | 'identity.intel'
    | 'mulehunter.feed'
    | 'mulehunter.screen'
    | 'mulehunter.case'
    | 'mulehunter.syndicates'
    | 'mulehunter.harvest'
    | 'telegram.intel'
    | 'instagram.osint'
    | 'fiat.statementflow'
    | 'p2p.intel'
    | 'watchlist'
    | 'bulk_import'
    | 'evidence.ingest'
    | 'legal.notices'
    | 'ai.narrative'
    | 'enrichment.jobs'
    | 'demo.cache_write'
    | 'settings.providers'
    | 'labels.global_write'
    | 'scrapers'
    | 'settings.cloud_vision'
    | 'settings.llm_cost'
    | 'admin.users'
    | 'admin.tools'
    | 'admin.super'
    | 'report.claim_check'
    | 'case.subject_link'
    | 'case.watch'
    | 'case.findings'
    | 'case.allowlist'
    | 'case.pivot'
    | 'case.review';

export interface FeatureGrant {
    allowed: boolean;
    /** Reserved for a future quota layer; always null in phase 1. */
    limit: number | null;
}

export type FeatureMatrix = Partial<Record<string, FeatureGrant>>;

export const TRIAL_TIER = 'trial';
export const FULL_ACCESS_EMAIL = 'cryptoflow@outris.com';

/** `mailto:` for an access request about one named feature. */
export function accessRequestMailto(featureName: string): string {
    return `mailto:${FULL_ACCESS_EMAIL}?subject=${encodeURIComponent(`CryptoFlow access request: ${featureName}`)}`;
}

/** The general request (trial banner, 403 toast) — same format as the per-feature one. */
export const FULL_ACCESS_MAILTO = accessRequestMailto('full access');

/**
 * Decide one feature from a /auth/me payload.
 *
 * When the payload carries no `features` (an older backend, or the profile
 * call failed) admin consoles fall back to the role, and everything else is
 * shown — the API still enforces, so this only affects what is rendered.
 */
export function isFeatureAllowed(
    features: FeatureMatrix | null | undefined,
    role: string | null | undefined,
    key: FeatureKey,
): boolean {
    const grant = features?.[key];
    if (grant) return grant.allowed === true;
    if (features) return false; // matrix present but key unknown → deny
    if (key === 'admin.super') return role === 'super_admin';
    if (key === 'admin.users' || key === 'admin.tools') return role === 'admin' || role === 'super_admin';
    return true;
}

interface MeLike {
    role?: string | null;
    tier?: string | null;
    features?: FeatureMatrix | null;
}

interface FeatureState {
    tier: string | null;
    role: string | null;
    features: FeatureMatrix | null;
    loaded: boolean;
    setFromProfile: (me: MeLike | null | undefined) => void;
    clear: () => void;
}

export const useFeatureStore = create<FeatureState>((set) => ({
    tier: null,
    role: null,
    features: null,
    loaded: false,
    setFromProfile: (me) => set({
        tier: me?.tier ?? me?.role ?? null,
        role: me?.role ?? null,
        features: me?.features ?? null,
        loaded: true,
    }),
    clear: () => set({ tier: null, role: null, features: null, loaded: false }),
}));

/**
 * Subscribe to a slice of the feature store. Uses the live state for the
 * server snapshot too (zustand's default is the initial state), so static
 * renders and tests see the same answer as the browser. Keep selectors
 * returning primitives or existing references.
 */
export function useFeatureState<T>(selector: (s: FeatureState) => T): T {
    const read = () => selector(useFeatureStore.getState());
    return useSyncExternalStore(useFeatureStore.subscribe, read, read);
}

/** True when the signed-in user may use `key`. */
export function useFeature(key: FeatureKey): boolean {
    return useFeatureState((s) => isFeatureAllowed(s.features, s.role, key));
}

/** The caller's tier (`trial`, `officer`, …) once /auth/me has loaded. */
export function useTier(): string | null {
    return useFeatureState((s) => s.tier);
}

export function useIsTrial(): boolean {
    return useFeatureState((s) => s.tier === TRIAL_TIER);
}

/** Non-hook read for callbacks and services. */
export function hasFeature(key: FeatureKey): boolean {
    const s = useFeatureStore.getState();
    return isFeatureAllowed(s.features, s.role, key);
}

export interface FeatureDeniedDetail {
    code: 'feature_denied';
    feature: string;
    tier: string;
}

/** Recognise the backend's 403 body: `{detail: {code: 'feature_denied', feature, tier}}`. */
export function featureDeniedFrom(err: unknown): FeatureDeniedDetail | null {
    // Already decoded by the axios interceptor (which also rewrites `detail`).
    const decoded = (err as { featureDenied?: FeatureDeniedDetail } | null)?.featureDenied;
    if (decoded) return decoded;
    const response = (err as { response?: { status?: number; data?: { detail?: unknown } } })?.response;
    if (!response || response.status !== 403) return null;
    const detail = response.data?.detail as Partial<FeatureDeniedDetail> | undefined;
    if (detail && typeof detail === 'object' && detail.code === 'feature_denied' && typeof detail.feature === 'string') {
        return { code: 'feature_denied', feature: detail.feature, tier: String(detail.tier ?? '') };
    }
    return null;
}

const FEATURE_LABELS: Partial<Record<string, string>> = {
    'investigation.auto': 'Auto-investigation',
    'osint.live_paid': 'Live OSINT lookups',
    'identity.intel': 'Identity intelligence',
    'mulehunter.feed': 'MuleHunter',
    'mulehunter.screen': 'MuleHunter screening',
    'mulehunter.case': 'MuleHunter case scan',
    'mulehunter.syndicates': 'Syndicate operations',
    'mulehunter.harvest': 'MuleHunter harvest',
    'telegram.intel': 'Telegram intelligence',
    'instagram.osint': 'Instagram OSINT',
    'fiat.statementflow': 'Fiat off-ramp',
    'p2p.intel': 'P2P intelligence',
    watchlist: 'Watchlist and flags',
    bulk_import: 'Bulk import',
    'evidence.ingest': 'Evidence ingestion',
    'legal.notices': 'Legal notices',
    'ai.narrative': 'AI narrative',
    'enrichment.jobs': 'Entity enrichment jobs',
    'settings.providers': 'Provider settings',
    'labels.global_write': 'Global labels and risk rules',
    scrapers: 'Scrapers',
};

export function featureLabel(key: string): string {
    return FEATURE_LABELS[key] ?? key;
}

/**
 * One factual line per gated feature, shown in the locked-item dialog and on
 * the trial page. `/auth/me` serves only `{allowed, limit}` per key, so the
 * copy lives here. Wording follows PRODUCT_DOCUMENT.md where it describes the
 * surface.
 */
export const FEATURE_BLURBS: Partial<Record<FeatureKey, string>> = {
    'investigation.auto': 'Runs the full auto-investigation of an address (exchange detection, velocity, counterparties, multi-hop exposure, backward and forward traces) and the Fusion case canvas operators.',
    'osint.live_paid': 'Live lookups of phones, handles and wallets against paid OSINT providers.',
    'identity.intel': 'Resolves an address to the people, phones and handles linked to it.',
    'mulehunter.feed': 'Searches the harvested mule index — phones, UPI IDs and bank accounts — by type, source and risk.',
    'mulehunter.screen': 'Runs our mule intelligence against your own file of identifiers (up to 5,000 rows).',
    'mulehunter.case': 'Scans a case for identifiers already in the mule index and promotes matches to the case graph.',
    'mulehunter.syndicates': 'Clusters of mule identifiers that recur together, with their members and a lookup.',
    'mulehunter.harvest': 'Runs and monitors the collectors that feed the mule index.',
    'telegram.intel': 'Monitored Telegram channels with their top senders, actor labels and geography.',
    'instagram.osint': 'Turns Instagram accounts into leads — posts, profiles and the identifiers in them — and compares accounts side by side.',
    'fiat.statementflow': 'Follows crypto off-ramps into bank statements to show where the fiat landed.',
    'p2p.intel': 'P2P merchant dossiers across exchanges, counterparty fingerprints and per-phone OSINT.',
    watchlist: 'Watches addresses and raises alerts and flags when they move funds.',
    bulk_import: 'Imports many addresses or a forensic export into the Workbench at once.',
    'evidence.ingest': 'Parses exchange records, device extractions and images into case evidence.',
    'legal.notices': 'Drafts notices to exchanges and tracks each notice through its lifecycle.',
    'ai.narrative': 'Writes an AI narrative of the investigation findings for the report.',
    'enrichment.jobs': 'Runs background jobs that add entity labels from external providers.',
    'settings.providers': 'Configures and monitors the external data providers.',
    'labels.global_write': 'Edits the labels and risk rules shared by every user.',
    scrapers: 'Manages the scrapers and data sources that feed the intelligence database.',
};

export function featureBlurb(key: string): string | null {
    return FEATURE_BLURBS[key as FeatureKey] ?? null;
}

/** Tooltip / title on a locked sidebar item. */
export const LOCKED_FEATURE_TITLE = 'Not included in your trial — contact us for access';
/** What a trial account can use — shared by the locked dialog and the trial page. */
export const TRIAL_SCOPE_SENTENCE =
    'Your trial covers address triage and tracing, your own cases and reports.';

export function featureDeniedMessage(feature: string, tier: string): string {
    const where = tier === TRIAL_TIER ? 'on a trial account' : 'for your role';
    return `${featureLabel(feature)} is not available ${where}.`;
}
